import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Stack,
    Divider,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";

export default function FeatureGateDialog({
    open,
    onClose,
    title,
    message,
    isLoggedIn,
    require = "premium", // "auth" | "premium"
    lang = "en",
    onLogin,
    onRegister,
    onUpgrade,
}) {
    const isEL = lang === "el" || lang === "gr";

    const defaults = {
        auth: {
            title: isEL ? "Απαιτείται λογαριασμός" : "Account required",
            chip: isEL ? "Σύνδεση/Εγγραφή" : "Login / Sign up",
            icon: <LoginRoundedIcon />,
            cta: isEL ? "Σύνδεση" : "Login",
            secondary: isEL ? "Εγγραφή" : "Sign up",
            msg:
                isEL
                    ? "Για να χρησιμοποιήσεις αυτή τη λειτουργία χρειάζεται να συνδεθείς ή να δημιουργήσεις λογαριασμό."
                    : "To use this feature, please log in or create an account.",
        },
        premium: {
            title: isEL ? "Premium λειτουργία" : "Premium feature",
            chip: isEL ? "Premium" : "Premium",
            icon: <WorkspacePremiumRoundedIcon />,
            cta: isEL ? "Αναβάθμιση σε Premium" : "Upgrade to Premium",
            secondary: isEL ? "Δες πλάνα" : "View plans",
            msg:
                isEL
                    ? "Η αλλαγή μάρκας/καταστήματος είναι διαθέσιμη μόνο για Premium χρήστες."
                    : "Changing brand/store is available only for Premium users.",
        },
    };

    const mode = require === "auth" ? defaults.auth : defaults.premium;

    const finalTitle = title || mode.title;
    const finalMessage = message || mode.msg;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle sx={{ pb: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2.5,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "action.hover",
                        }}
                    >
                        {require === "premium" ? <StarRoundedIcon /> : <LockRoundedIcon />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {finalTitle}
                        </Typography>
                        <Chip
                            size="small"
                            label={mode.chip}
                            sx={{ mt: 0.75, fontWeight: 700 }}
                            color={require === "premium" ? "warning" : "primary"}
                            variant="outlined"
                        />
                    </Box>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent dividers sx={{ pt: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                    {finalMessage}
                </Typography>

                {!isLoggedIn && require === "premium" && (
                    <Typography variant="body2" sx={{ mt: 2, fontWeight: 700 }}>
                        {lang === "el" || lang === "gr"
                            ? "Σύνδεση για να δεις τα Premium πλάνα σου."
                            : "Log in to view your Premium options."}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
                    {isEL ? "Κλείσιμο" : "Close"}
                </Button>

                {!isLoggedIn ? (
                    <>
                        <Button
                            onClick={onLogin}
                            variant="contained"
                            startIcon={<LoginRoundedIcon />}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        >
                            {isEL ? "Σύνδεση" : "Login"}
                        </Button>

                        <Button
                            onClick={onRegister}
                            variant="text"
                            startIcon={<PersonAddAltRoundedIcon />}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        >
                            {isEL ? "Εγγραφή" : "Sign up"}
                        </Button>
                    </>
                ) : require === "premium" ? (
                    <Button
                        onClick={onUpgrade}
                        variant="contained"
                        startIcon={<WorkspacePremiumRoundedIcon />}
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                        {mode.cta}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
